// What the signed-in user may see in the UI (ADR 0010). The API checks every
// request anyway; this only hides what would answer 403.
import type { Permissions, User } from '../api/types.js';

export interface NavItem {
  to: string;
  label: string;
}

/**
 * Top navigation by permission: tenant users see Sites, Points, Alarms
 * (and Users / API tokens as tenant admins); platform staff also Tenants
 * and Firewall. Alarms is rendered separately (it carries a counter).
 */
export function navItems(p: Permissions): NavItem[] {
  const items: NavItem[] = [];
  if (p.read) items.push({ to: '/sites', label: 'Sites' }, { to: '/points', label: 'Points' });
  if (p.tenant_admin) {
    items.push({ to: '/settings/users', label: 'Users' });
    items.push({ to: '/settings/api-tokens', label: 'API tokens' });
  }
  if (p.platform) {
    items.push({ to: '/tenants', label: 'Tenants' }, { to: '/firewall', label: 'Firewall' });
  }
  return items;
}

/** Platform staff: infrastructure (edges, enrollment, updates, firewall, LoRaWAN setup). */
export const isPlatform = (u: User | null): boolean => u?.permissions.platform ?? false;

/** May the user change data of this site (rules, holidays, tags, alarm ack)? */
export function canOperateSite(u: User | null, site: { id: string; tenant: { id: string } }) {
  if (!u) return false;
  if (u.permissions.platform) return true;
  return (u.tenants ?? []).some(
    (t) =>
      t.id === site.tenant.id &&
      t.role !== 'viewer' &&
      (t.site_ids === null || t.site_ids.includes(site.id)),
  );
}

/** Tenants the user administers (users and tokens), with whether all their sites are its own. */
export function adminTenants(u: User | null): { id: string; name: string; allSites: boolean }[] {
  return (u?.tenants ?? [])
    .filter((t) => t.role === 'tenant_admin')
    .map((t) => ({ id: t.id, name: t.name, allSites: t.site_ids === null }));
}
